/**
 * useDealProposals - Propostas comerciais vinculadas a um negócio
 *
 * Todo acesso a dados é feito por RPCs SECURITY DEFINER
 * (get_deal_proposals / get_proposal_details / create_deal_proposal /
 * update_proposal_status), nunca via SELECT direto na tabela.
 *
 * @version 1.5.0
 *          do negócio refletir o aceite/recusa da proposta na hora (sem F5)
 * RELEASE: 2026-09-03 v1.5.0 - Bloqueios do Workflow passam a abrir o aviso
 *          global (modal), sem toast vermelho de erro inesperado.
 * RELEASE: 2026-08-12 v1.1.0 - invalidate() também limpa `deal-has-accepted-proposal`
 *          e `deal-modal-data`, para o bloqueio da aba Produtos valer na hora
 *          em que a proposta é aceita/cancelada (sem precisar de F5)
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (Fase 3 do módulo de Propostas)

 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';

const logger = createLogger('useDealProposals');

/** Situações possíveis de uma proposta (espelha o enum crm_proposal_status) */
export type ProposalStatus =
  | 'draft'
  | 'generated'
  | 'sent'
  | 'viewed'
  | 'accepted'
  | 'rejected'
  | 'expired'
  | 'cancelled'
  | 'superseded';

/** Rótulos em PT-BR das situações */
export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: 'Rascunho',
  generated: 'Gerada',
  sent: 'Enviada',
  viewed: 'Visualizada',
  accepted: 'Aceita',
  rejected: 'Recusada',
  expired: 'Expirada',
  cancelled: 'Cancelada',
  superseded: 'Substituída',
};

/** Cor semântica (bolinha) de cada situação — padrão de status do sistema */
export const PROPOSAL_STATUS_COLORS: Record<ProposalStatus, string> = {
  draft: 'bg-muted-foreground',
  generated: 'bg-blue-500',
  sent: 'bg-indigo-500',
  viewed: 'bg-amber-500',
  accepted: 'bg-emerald-500',
  rejected: 'bg-destructive',
  expired: 'bg-orange-500',
  cancelled: 'bg-muted-foreground',
  superseded: 'bg-muted-foreground',
};

/** Linha retornada por get_deal_proposals */
export interface DealProposal {
  id: string;
  proposal_number: string;
  version_number: number;
  title: string | null;
  status: ProposalStatus;
  currency: string;
  subtotal: number;
  discount_total: number;
  total: number;
  mrr_total: number;
  items_count: number;
  valid_from: string;
  expires_at: string | null;
  is_expired: boolean;
  /** true quando a aba Produtos mudou depois desta proposta */
  is_out_of_sync: boolean;
  pdf_status: string;
  pdf_storage_path: string | null;
  public_enabled: boolean;
  /** Token do link público ativo (null quando o link está desativado) */
  public_token?: string | null;
  sent_at: string | null;
  first_viewed_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  created_by_name: string | null;
  created_at: string;
  /** v1.9.0: tipo da proposta (cadastro crm_proposal_types) */
  proposal_type_id?: string | null;
  proposal_type_name?: string | null;
  proposal_type_color?: string | null;
  total_count: number;
}

/** Payload de criação de proposta */
export interface CreateProposalInput {
  dealId: string;
  title?: string | null;
  description?: string | null;
  validDays?: number;
  templateId?: string | null;
  /** v1.9.0: tipo da proposta (obrigatório no servidor) */
  proposalTypeId?: string | null;
}

/**
 * Lista as propostas de um negócio (paginação server-side).
 */
export function useDealProposals(dealId: string | null, enabled = true, limit = 20, offset = 0) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['deal-proposals', tenantId, dealId, limit, offset],
    enabled: enabled && !!dealId && !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<DealProposal[]> => {
      const { data, error } = await (supabase.rpc as any)('get_deal_proposals', {
        p_deal_id: dealId,
        p_limit: limit,
        p_offset: offset,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar propostas', { error: error.message });
        throw error;
      }
      return (data || []) as DealProposal[];
    },
  });

  /**
   * Invalida listagem e contadores dependentes.
   * v1.2.0: inclui as chaves da timeline de atividades do negócio (os eventos
   * de proposta são gravados em crm_deal_activities pela trigger).
   * v1.1.0: inclui `deal-has-accepted-proposal`, pois a situação da proposta
   * define o bloqueio de edição da aba Produtos (precisa refletir na hora).
   */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['deal-proposals'] });
    queryClient.invalidateQueries({ queryKey: ['deal-proposal-details'] });
    queryClient.invalidateQueries({ queryKey: ['deal-has-accepted-proposal'] });
    queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });
    queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated'] });
    queryClient.invalidateQueries({ queryKey: ['crm-deal-activities'] });
    // v1.3.0: aba Propostas dos modais de Cliente/Contato (listagem + contador)
    queryClient.invalidateQueries({ queryKey: ['customer-proposals'] });
    queryClient.invalidateQueries({ queryKey: ['customer-modal-data'] });
    // v1.4.0: esteira (Workflow) do negócio — eventos proposal.* mudam a etapa atual,
    // então a barra do modal precisa recarregar sem depender de F5.
  };



  /** Gera uma nova proposta a partir dos produtos atuais do negócio */
  const createProposal = useMutation({
    mutationFn: async (input: CreateProposalInput) => {
      const { data, error } = await (supabase.rpc as any)('create_deal_proposal', {
        p_deal_id: input.dealId,
        p_title: input.title ?? null,
        p_description: input.description ?? null,
        p_valid_days: input.validDays ?? 15,
        p_template_id: input.templateId ?? null,
        // v1.9.0: tipo da proposta (validado no servidor)
        p_proposal_type_id: input.proposalTypeId ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Proposta gerada com sucesso');
    },
    onError: (error: any) => {
      logger.error('Erro ao gerar proposta', { error: error?.message });
      // Bloqueio do Workflow é feedback de processo, não erro inesperado.
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      toast.error(error?.message || 'Não foi possível gerar a proposta');
    },
  });

  /** Atualiza a situação da proposta (enviar, aceitar, recusar, cancelar) */
  const updateStatus = useMutation({
    mutationFn: async (params: {
      proposalId: string;
      status: 'sent' | 'accepted' | 'rejected' | 'cancelled';
      reason?: string | null;
      actorName?: string | null;
      actorEmail?: string | null;
    }) => {
      const { data, error } = await (supabase.rpc as any)('update_proposal_status', {
        p_proposal_id: params.proposalId,
        p_new_status: params.status,
        p_reason: params.reason ?? null,
        p_actor_name: params.actorName ?? null,
        p_actor_email: params.actorEmail ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Situação da proposta atualizada');
    },
    onError: (error: any) => {
      logger.error('Erro ao atualizar situação', { error: error?.message });
      // Bloqueio do Workflow é feedback de processo, não erro inesperado.
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      toast.error(error?.message || 'Não foi possível atualizar a proposta');
    },
  });

  return {
    proposals: query.data ?? [],
    totalCount: query.data?.[0]?.total_count ?? 0,
    isLoading: query.isLoading,
    refetch: query.refetch,
    createProposal,
    updateStatus,
  };
}

/**
 * Detalhes completos de uma proposta (snapshot de itens + histórico + eventos).
 */
export function useProposalDetails(proposalId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['deal-proposal-details', tenantId, proposalId],
    enabled: enabled && !!proposalId && !!tenantId,
    staleTime: 15_000,
    // Rede de segurança: enquanto o modal está aberto, revalida a cada 30s caso
    // o canal de Realtime caia (engajamento/histórico sempre atualizados).
    refetchInterval: enabled && proposalId ? 30_000 : false,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_proposal_details', {
        p_proposal_id: proposalId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar proposta', { error: error.message });
        throw error;
      }
      return data as any;
    },
  });
}

/**
 * useProposalPdf - Geração e download do PDF da proposta (Fase 5)
 *
 * Fluxo:
 *  1. `request_proposal_pdf` marca a proposta como enfileirada (validação de tenant);
 *  2. Edge Function `generate-proposal-pdf` renderiza o snapshot e salva no Storage;
 *  3. O download usa signed URL do bucket privado `contratos`.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial
 */
export function useProposalPdf() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  /** Dispara a geração do PDF (síncrona do ponto de vista do usuário) */
  const generatePdf = useMutation({
    mutationFn: async (proposalId: string) => {
      // Passo 1: marca como enfileirada — bloqueia duplo clique no servidor
      const { error: requestError } = await (supabase.rpc as any)('request_proposal_pdf', {
        p_proposal_id: proposalId,
        p_tenant_hint: tenantId ?? null,
      });
      if (requestError) throw requestError;

      // Passo 2: renderização + upload
      const { data, error } = await supabase.functions.invoke('generate-proposal-pdf', {
        body: { proposal_id: proposalId },
      });
      if (error) {
        // Extrai a mensagem real retornada pela Edge Function
        const details =
          typeof (error as any)?.context?.text === 'function'
            ? await (error as any).context.text()
            : error.message;
        logger.error('Falha ao gerar PDF da proposta', { details });
        throw new Error(details || 'Não foi possível gerar o PDF');
      }
      return data as { storage_path: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['deal-proposal-details'] });
      toast.success('PDF da proposta gerado com sucesso');
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ['deal-proposals'] });
      toast.error(error?.message || 'Não foi possível gerar o PDF');
    },
  });

  /**
   * Abre o PDF em nova aba usando signed URL temporária (bucket privado).
   * Memoizado com useCallback para manter identidade estável entre renders.
   */
  const openPdf = useCallback(async (storagePath: string | null) => {
    if (!storagePath) {
      toast.error('PDF ainda não disponível');
      return;
    }
    const { data, error } = await supabase.storage
      .from('contratos')
      .createSignedUrl(storagePath, 3600);

    if (error || !data?.signedUrl) {
      logger.error('Erro ao criar link do PDF', { error: error?.message });
      toast.error('Não foi possível abrir o PDF');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  }, []);

  /**
   * Retorna a signed URL temporária do PDF (usada pelo preview interno).
   * Diferente de openPdf, NÃO abre nova aba — apenas devolve o link.
   * IMPORTANTE: memoizado (useCallback) — sem isso, a identidade mudava a cada
   * render e provocava loop infinito de createSignedUrl no modal de preview.
   */
  const getPdfSignedUrl = useCallback(
    async (storagePath: string | null): Promise<string | null> => {
      if (!storagePath) return null;
      const { data, error } = await supabase.storage
        .from('contratos')
        .createSignedUrl(storagePath, 3600);

      if (error || !data?.signedUrl) {
        logger.error('Erro ao criar link do PDF para preview', { error: error?.message });
        return null;
      }
      return data.signedUrl;
    },
    [],
  );

  return { generatePdf, openPdf, getPdfSignedUrl };


}

/** Monta a URL pública da proposta a partir do token */
export function buildPublicProposalUrl(token: string): string {
  return `${window.location.origin}/p/${token}`;
}

/**
 * useProposalShare - Geração do link público e marcação de envio (Fase 6)
 *
 * O token é persistido em `crm_proposals.public_token`, de forma que o MESMO
 * link pode ser copiado quantas vezes for necessário. Quando já existe link
 * ativo, a RPC reaproveita o token (não invalida o link do cliente).
 *
 * IMPORTANTE (v2.0.0 da RPC): gerar/ativar o link NÃO marca mais a proposta
 * como "enviada". Passe `markAsSent: true` apenas quando houver envio real ao
 * cliente (chat/WhatsApp) ou compartilhamento explícito do link.
 *
 * @version 1.2.0
 * RELEASE: 2026-08-12 v1.2.0 - Novo parâmetro `markAsSent` (p_mark_as_sent) e
 *          invalidação das chaves da timeline de atividades do negócio
 * RELEASE: 2026-08-11 v1.1.0 - Link persistente + helper buildPublicProposalUrl
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial
 */
export function useProposalShare() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();
  const tenantId = currentTenant?.id;

  const sendProposal = useMutation({
    mutationFn: async (params: {
      proposalId: string;
      expiresAt?: string | null;
      /** true somente quando houve envio real / compartilhamento explícito */
      markAsSent?: boolean;
    }) => {
      const { data, error } = await (supabase.rpc as any)('send_deal_proposal', {
        p_proposal_id: params.proposalId,
        p_expires_at: params.expiresAt ?? null,
        p_mark_as_sent: params.markAsSent ?? false,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;

      const token = (data as any)?.token as string | undefined;
      if (!token) throw new Error('Não foi possível gerar o link público');

      return {
        token,
        url: `${window.location.origin}/p/${token}`,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['deal-proposal-details'] });
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });
      // Timeline do negócio (evento de link gerado / proposta enviada)
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities'] });

    },
    onError: (error: any) => {
      logger.error('Erro ao gerar link público da proposta', { error: error?.message });
      // Bloqueio do Workflow é feedback de processo, não erro inesperado.
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      toast.error(error?.message || 'Não foi possível gerar o link público');
    },
  });

  return { sendProposal };
}


/**
 * useCreateProposalWithLink - Criação de proposta "pronta para compartilhar"
 *
 * Compõe as rotinas JÁ existentes (nenhuma RPC nova):
 *   1. `create_deal_proposal`  → gera o snapshot dos itens do negócio;
 *   2. `send_deal_proposal`    → ativa/reaproveita o link público (token persistente);
 *   3. `generate-proposal-pdf` → deixa o PDF pronto para o compartilhamento.
 *
 * Os passos 2 e 3 são best-effort: qualquer falha neles NÃO invalida a proposta
 * já criada (o usuário consegue gerar link/PDF manualmente na aba Propostas).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial (proposta automática ao criar negócio)
 */
export interface CreatedProposalWithLink {
  proposalId: string;
  /** Número legível (PROP-XXXXXXXX) quando disponível */
  proposalNumber: string | null;
  /** URL pública já ativa (null quando a geração do link falhou) */
  publicUrl: string | null;
}

export function useCreateProposalWithLink() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  /**
   * Cria a proposta e já devolve o link público.
   * @param input dados da proposta (mesmo payload de create_deal_proposal)
   * @param options `withPdf` dispara a geração do PDF em background (default true)
   */
  const createWithLink = useCallback(
    async (
      input: CreateProposalInput,
      options?: { withPdf?: boolean },
    ): Promise<CreatedProposalWithLink> => {
      // PASSO 1: cria a proposta (snapshot dos produtos/serviços do negócio)
      const { data: proposalId, error } = await (supabase.rpc as any)('create_deal_proposal', {
        p_deal_id: input.dealId,
        p_title: input.title ?? null,
        p_description: input.description ?? null,
        p_valid_days: input.validDays ?? 15,
        p_template_id: input.templateId ?? null,
        // v1.9.0: tipo da proposta (validado no servidor)
        p_proposal_type_id: input.proposalTypeId ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;

      const id = proposalId as string;
      let publicUrl: string | null = null;
      let proposalNumber: string | null = null;

      // PASSO 2: ativa o link público (best-effort)
      try {
        const { data: sendData, error: sendError } = await (supabase.rpc as any)(
          'send_deal_proposal',
          {
            p_proposal_id: id,
            p_expires_at: null,
            // Ativação automática do link NÃO marca a proposta como enviada
            p_mark_as_sent: false,
            p_tenant_hint: tenantId ?? null,
          },
        );
        if (sendError) throw sendError;
        const token = (sendData as any)?.token as string | undefined;
        proposalNumber = ((sendData as any)?.proposal_number as string | undefined) ?? null;
        if (token) publicUrl = buildPublicProposalUrl(token);
      } catch (linkError: any) {
        logger.warn('Proposta criada, mas o link público falhou', {
          error: linkError?.message,
        });
      }

      // PASSO 3: PDF em background (não bloqueia o usuário)
      if (options?.withPdf !== false) {
        void (async () => {
          try {
            await (supabase.rpc as any)('request_proposal_pdf', {
              p_proposal_id: id,
              p_tenant_hint: tenantId ?? null,
            });
            await supabase.functions.invoke('generate-proposal-pdf', {
              body: { proposal_id: id },
            });
            queryClient.invalidateQueries({ queryKey: ['deal-proposals'] });
            queryClient.invalidateQueries({ queryKey: ['deal-proposal-details'] });
          } catch (pdfError: any) {
            logger.warn('Falha ao gerar PDF automático da proposta', {
              error: pdfError?.message,
            });
          }
        })();
      }

      queryClient.invalidateQueries({ queryKey: ['deal-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['deal-proposal-details'] });
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });



      return { proposalId: id, proposalNumber, publicUrl };
    },
    [queryClient, tenantId],
  );

  return { createWithLink };
}

/**
 * Linha retornada por get_customer_proposals — mesma projeção de
 * get_deal_proposals acrescida do negócio de origem.
 */
export interface CustomerProposal extends DealProposal {
  deal_id: string;
  deal_title: string | null;
}

/**
 * useCustomerProposals - Propostas de TODOS os negócios de um cliente/contato
 *
 * Usada pela aba "Propostas" dos modais de Cliente e Contato. A RPC
 * `get_customer_proposals` aplica `can_user_view_crm_deal` por negócio,
 * portanto a listagem já vem filtrada pelas regras de acesso do CRM.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
export function useCustomerProposals(
  customerId: string | null,
  contactId: string | null,
  enabled = true,
  limit = 50,
  offset = 0,
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['customer-proposals', tenantId, customerId, contactId, limit, offset],
    enabled: enabled && (!!customerId || !!contactId) && !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<CustomerProposal[]> => {
      const { data, error } = await (supabase.rpc as any)('get_customer_proposals', {
        p_customer_id: customerId,
        p_contact_id: contactId,
        p_limit: limit,
        p_offset: offset,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar propostas do cliente', { error: error.message });
        throw error;
      }
      return (data || []) as CustomerProposal[];
    },
  });

  return {
    proposals: query.data ?? [],
    totalCount: query.data?.[0]?.total_count ?? 0,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
